/**
 * HeroImage - Core reusable brand illustration component
 *
 * Flexible display modes:
 * - Normal: Full opacity with bottom fade
 * - Faded: Low opacity (0.08-0.15) for backgrounds
 * - Frost: Frosted glass overlay for paywall
 * - Broken: Clip-path gap with dripping paint for 404
 */

import { useState, useEffect } from 'react';
import { BRAND } from '@/styles/tokens';
import type { HeroImageProps } from '@/types/brand';

export function HeroImage({
  src,
  height = 260,
  objectPosition = 'center 45%',
  fadeColor = BRAND.cream,
  fadeToWhite = false,
  opacity = 1,
  frost = false,
  broken = false,
  children,
  style,
  className = '',
}: HeroImageProps) {
  const [loaded, setLoaded] = useState(false);

  // Reset loaded state when src changes
  useEffect(() => {
    setLoaded(false);
  }, [src]);

  return (
    <div
      style={{
        position: 'relative',
        height: typeof height === 'number' ? `${height}px` : height,
        overflow: 'hidden',
        ...style,
      }}
      className={className}
    >
      {/* Main image */}
      {broken ? (
        <>
          <style>{`
            .broken-clip {
              clip-path: polygon(
                0 0, 100% 0, 100% 42%,
                58% 42%, 58% 100%, 42% 100%, 42% 42%,
                0 42%
              );
            }
          `}</style>
          <img
            src={src}
            alt=""
            role="presentation"
            onLoad={() => setLoaded(true)}
            className="broken-clip"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition,
              opacity: loaded ? opacity : 0,
              transition: 'opacity 0.6s ease',
            }}
          />
          {/* Drip SVG overlay */}
          <svg
            viewBox="0 0 100 200"
            style={{
              position: 'absolute',
              top: 0,
              left: '42%',
              width: '16%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            {/* Teal drip */}
            <g>
              <line
                x1="25"
                y1="30"
                x2="25"
                y2="80"
                stroke={BRAND.teal}
                strokeWidth="4"
                strokeLinecap="round"
                opacity="0.35"
              />
              <circle cx="25" cy="86" r="5" fill={BRAND.teal} opacity="0.25" />
            </g>
            {/* Coral drip */}
            <g>
              <line
                x1="50"
                y1="50"
                x2="50"
                y2="90"
                stroke={BRAND.coral}
                strokeWidth="4"
                strokeLinecap="round"
                opacity="0.35"
              />
              <circle cx="50" cy="96" r="5" fill={BRAND.coral} opacity="0.25" />
            </g>
            {/* Gold drip */}
            <g>
              <line
                x1="70"
                y1="70"
                x2="70"
                y2="125"
                stroke={BRAND.goldWarm}
                strokeWidth="4"
                strokeLinecap="round"
                opacity="0.35"
              />
              <circle cx="70" cy="131" r="5" fill={BRAND.goldWarm} opacity="0.25" />
            </g>
          </svg>
        </>
      ) : (
        <img
          src={src}
          alt=""
          role="presentation"
          onLoad={() => setLoaded(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition,
            opacity: loaded ? opacity : 0,
            transition: 'opacity 0.6s ease',
          }}
        />
      )}

      {/* Bottom gradient fade */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '50%',
          background: `linear-gradient(to bottom, transparent, ${
            fadeToWhite ? '#FFFFFF' : fadeColor
          })`,
          pointerEvents: 'none',
        }}
      />

      {/* Frost overlay (paywall) */}
      {frost && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(135deg, rgba(250,248,243,0.73), rgba(255,255,255,0.53))',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)', // Safari support
          }}
        />
      )}

      {/* Overlay content */}
      {children && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export default HeroImage;
